/**
 * CompanionWindow — COPILOT_MASTER_PLAN Phase 1 / subsystem A.
 *
 * The floating AI-Companion chrome: a draggable (pointer-drag on header),
 * dockable (float | right | bottom), resizable, minimizable window that
 * re-houses the existing copilot body (chat stream, agent panel, HITL chips,
 * feature panels) without changing any of its plumbing.
 *
 * - dock='right'  → the legacy Sheet behavior (full-height right panel).
 * - dock='bottom' → full-width bottom panel.
 * - dock='float'  → free window, drag via header, resize via SE handle,
 *                   drag-release near the right/bottom edge snaps to a dock.
 * - <640px viewports → always a full-width bottom sheet (responsive
 *   fallback; no drag/resize) so there is never horizontal overflow.
 *
 * Position/size/dock/mode are persisted per user in
 * localStorage['sgrc-companion-window'] (useCompanionWindowStore).
 *
 * Unlike the old Sheet there is NO modal overlay: the page behind stays
 * fully interactive while the Companion is open.
 *
 * ── Modal co-existence (subsystem A fix) ──────────────────────────────────────
 * A create/edit MODAL must be fully usable regardless of the Companion state.
 * The C2 shell shipped the window at z-[75], ABOVE Radix dialog content (z-50),
 * so a docked-open Companion occluded the right ~25% of the viewport — including
 * a create modal's submit/action buttons — and the create could not be
 * completed. Fix: `useAnyPageDialogOpen()` detects when a *page* Radix dialog
 * (Dialog / Sheet / AlertDialog — `[role=dialog][data-state=open]`, excluding
 * THIS window and the copilot HITL gate) is open and, while it is, the Companion
 * window drops below the Radix overlay (z-40 < z-50). The modal + its backdrop
 * then render above the Companion, so its submit button is fully visible and
 * clickable; when no page dialog is open the window sits at z-[75] and behaves
 * exactly as before. The Companion's OWN internal dialogs/popovers (RPA-save
 * Dialog, HITL gate, LLM selectors) are body-portals at z-50/z-[9998] and stay
 * above the z-40 window, so they keep working.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Sparkles, X, Minus, PanelRight, PictureInPicture2, MessageSquare, Bot } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useCompanionWindowStore,
  clampToViewport,
  COMPANION_WINDOW_DEFAULTS,
  type CompanionMode,
} from '@/stores/useCompanionWindowStore'

// ── Page-dialog detection (modal co-existence) ────────────────────────────────

/**
 * True while a *page-level* Radix dialog is open (a create/edit modal, an
 * alert-dialog, a side sheet). Radix Dialog/AlertDialog/Sheet content all render
 * `[role="dialog"][data-state="open"]`. We deliberately EXCLUDE:
 *   - the Companion window itself (`[data-copilot-id="companion-window"]`), and
 *   - the copilot HITL confirmation gate (`[data-hitl-gate]`),
 * so the Companion never "yields to itself". A MutationObserver keeps the value
 * live as dialogs mount/unmount or flip their `data-state`.
 */
function useAnyPageDialogOpen(): boolean {
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return

    const compute = () => {
      const nodes = document.querySelectorAll<HTMLElement>(
        '[role="dialog"][data-state="open"]',
      )
      let found = false
      nodes.forEach((n) => {
        if (found) return
        if (n.closest('[data-copilot-id="companion-window"]')) return // this window
        if (n.closest('[data-hitl-gate]')) return // copilot HITL gate
        found = true
      })
      setDialogOpen((prev) => (prev === found ? prev : found))
    }

    compute()
    const obs = new MutationObserver(compute)
    obs.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-state', 'role'],
    })
    return () => obs.disconnect()
  }, [])

  return dialogOpen
}

// ── Responsive fallback ───────────────────────────────────────────────────────

function useIsSmallViewport(): boolean {
  const [small, setSmall] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const onChange = (e: MediaQueryListEvent) => setSmall(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return small
}

// ── Assistant / Agent mode toggle (subsystem H — stored preference) ──────────

function ModeToggle() {
  const mode = useCompanionWindowStore((s) => s.mode)
  const setMode = useCompanionWindowStore((s) => s.setMode)

  const item = (m: CompanionMode, Icon: typeof MessageSquare, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      title={label}
      aria-pressed={mode === m}
      data-copilot-id={`companion-mode-${m}`}
      className={cn(
        'flex h-6 items-center justify-center gap-1 rounded px-1.5 text-[11px] font-medium transition-colors',
        mode === m
          ? 'bg-background text-primary shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="sr-only">{label}</span>
    </button>
  )

  return (
    <div
      className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted p-0.5"
      data-copilot-id="companion-mode-toggle"
      data-companion-mode={mode}
      title={mode === 'assistant' ? 'Mode Assistant (propose + confirme)' : 'Mode Agent (planifie + agit)'}
    >
      {item('assistant', MessageSquare, 'Mode Assistant')}
      {item('agent', Bot, 'Mode Agent')}
    </div>
  )
}

// ── Main window ───────────────────────────────────────────────────────────────

export interface CompanionWindowProps {
  /** Extra header icon buttons (RAG toggle, flow recorder, clear chat…). */
  headerActions?: React.ReactNode
  children: React.ReactNode
}

const SNAP_MARGIN = 32 // px from viewport edge (cursor position) that snaps a drag-release into a dock
const UNDOCK_THRESHOLD = 24 // px of drag movement that pulls a docked window out to float

export function CompanionWindow({ headerActions, children }: CompanionWindowProps) {
  const dock = useCompanionWindowStore((s) => s.dock)
  const x = useCompanionWindowStore((s) => s.x)
  const y = useCompanionWindowStore((s) => s.y)
  const w = useCompanionWindowStore((s) => s.w)
  const h = useCompanionWindowStore((s) => s.h)
  const setDock = useCompanionWindowStore((s) => s.setDock)
  const setPosition = useCompanionWindowStore((s) => s.setPosition)
  const setSize = useCompanionWindowStore((s) => s.setSize)
  const minimize = useCompanionWindowStore((s) => s.minimize)
  const closeWindow = useCompanionWindowStore((s) => s.closeWindow)

  const isSmall = useIsSmallViewport()
  // While a page-level create/edit modal is open, yield below its overlay so the
  // modal (and its submit button) render fully above the Companion.
  const pageDialogOpen = useAnyPageDialogOpen()
  const rootRef = useRef<HTMLDivElement>(null)

  // Re-clamp a persisted position that may be off-screen on this viewport.
  useEffect(() => {
    if (dock !== 'float' || isSmall) return
    const clamped = clampToViewport(x, y, w, h)
    if (clamped.x !== x || clamped.y !== y) setPosition(clamped.x, clamped.y)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dock, isSmall])

  // ── Drag (header) ──────────────────────────────────────────────────────────
  const dragRef = useRef<{
    pointerId: number
    startClientX: number
    startClientY: number
    originX: number
    originY: number
    floating: boolean
  } | null>(null)

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (isSmall || e.button !== 0) return
      // Never start a drag from an interactive control in the header.
      if ((e.target as HTMLElement).closest('button, input, select, textarea, a')) return
      const el = rootRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      dragRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        originX: rect.left,
        originY: rect.top,
        floating: dock === 'float',
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    [dock, isSmall],
  )

  const onHeaderPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current
      if (!d || d.pointerId !== e.pointerId) return
      const dx = e.clientX - d.startClientX
      const dy = e.clientY - d.startClientY

      // Pull a docked window out to float once the drag passes the threshold.
      if (!d.floating) {
        if (Math.abs(dx) < UNDOCK_THRESHOLD && Math.abs(dy) < UNDOCK_THRESHOLD) return
        d.floating = true
        const floatW = Math.min(w, window.innerWidth - 16)
        d.originX = Math.max(0, e.clientX - floatW / 2)
        d.originY = Math.max(0, e.clientY - 16)
        d.startClientX = e.clientX
        d.startClientY = e.clientY
        setDock('float')
        setPosition(d.originX, d.originY)
        return
      }

      const el = rootRef.current
      if (!el) return
      const next = clampToViewport(d.originX + dx, d.originY + dy, w, h)
      // Direct DOM write during the drag (no per-mousemove React re-render of
      // the heavy chat body); committed to the store on pointer-up.
      el.style.left = `${next.x}px`
      el.style.top = `${next.y}px`
    },
    [w, h, setDock, setPosition],
  )

  const onHeaderPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current
      if (!d || d.pointerId !== e.pointerId) return
      dragRef.current = null
      if (!d.floating) return
      const el = rootRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()

      // Edge snap: release with the CURSOR near the right/bottom edge docks.
      if (e.clientX >= window.innerWidth - SNAP_MARGIN) {
        setDock('right')
        return
      }
      if (e.clientY >= window.innerHeight - SNAP_MARGIN) {
        setDock('bottom')
        return
      }
      setPosition(rect.left, rect.top)
    },
    [setDock, setPosition],
  )

  // ── Resize (SE handle, float mode only) ───────────────────────────────────
  const resizeRef = useRef<{
    pointerId: number
    startClientX: number
    startClientY: number
    originW: number
    originH: number
  } | null>(null)

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()
      resizeRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        originW: w,
        originH: h,
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    [w, h],
  )

  const onResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const r = resizeRef.current
      if (!r || r.pointerId !== e.pointerId) return
      const el = rootRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const maxW = window.innerWidth - rect.left - 8
      const maxH = window.innerHeight - rect.top - 8
      const nextW = Math.min(Math.max(COMPANION_WINDOW_DEFAULTS.minW, r.originW + (e.clientX - r.startClientX)), maxW)
      const nextH = Math.min(Math.max(COMPANION_WINDOW_DEFAULTS.minH, r.originH + (e.clientY - r.startClientY)), maxH)
      el.style.width = `${nextW}px`
      el.style.height = `${nextH}px`
    },
    [],
  )

  const onResizePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const r = resizeRef.current
      if (!r || r.pointerId !== e.pointerId) return
      resizeRef.current = null
      const el = rootRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setSize(rect.width, rect.height)
    },
    [setSize],
  )

  // ── Geometry per dock mode ─────────────────────────────────────────────────
  const effectiveDock = isSmall ? 'sheet' : dock

  const style: React.CSSProperties =
    effectiveDock === 'float'
      ? { left: x, top: y, width: Math.min(w, window.innerWidth - 8), height: Math.min(h, window.innerHeight - 8) }
      : effectiveDock === 'right'
      ? { width: Math.min(Math.max(w, COMPANION_WINDOW_DEFAULTS.minW), window.innerWidth) }
      : effectiveDock === 'bottom'
      ? { height: Math.min(h, Math.round(window.innerHeight * 0.85)) }
      : {} // sheet — pure CSS

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Assistant IA"
      data-copilot-id="companion-window"
      data-companion-dock={effectiveDock}
      data-companion-yield={pageDialogOpen ? '' : undefined}
      className={cn(
        'fixed flex flex-col overflow-hidden border bg-background text-foreground shadow-2xl',
        // Normally above page content (z-[75]); while a page modal is open, drop
        // BELOW the Radix dialog overlay (z-50) so the modal + its submit button
        // render fully on top and stay clickable.
        pageDialogOpen ? 'z-40' : 'z-[75]',
        effectiveDock === 'float' && 'rounded-xl',
        effectiveDock === 'right' && 'inset-y-0 right-0 h-full max-w-full rounded-none border-y-0 border-r-0',
        effectiveDock === 'bottom' && 'inset-x-0 bottom-0 w-full rounded-t-xl border-x-0 border-b-0',
        effectiveDock === 'sheet' && 'inset-x-0 bottom-0 h-[75vh] max-h-[85vh] w-full rounded-t-xl border-x-0 border-b-0',
      )}
      style={style}
    >
      {/* Header — drag handle + mode toggle + window controls */}
      <div
        data-copilot-id="companion-header"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        className={cn(
          'flex shrink-0 select-none items-center gap-1.5 overflow-hidden border-b bg-muted/40 px-2.5 py-2',
          !isSmall && 'cursor-grab active:cursor-grabbing',
        )}
        style={{ touchAction: 'none' }}
      >
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <span className="hidden min-w-0 truncate text-sm font-semibold min-[360px]:inline">
          Copilot
        </span>

        <ModeToggle />

        <div className="min-w-0 flex-1" />

        <div className="flex shrink-0 items-center gap-1">
          {headerActions}
          {!isSmall && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title={dock === 'float' ? 'Ancrer à droite' : 'Détacher (fenêtre flottante)'}
              data-copilot-id="companion-dock-toggle"
              onClick={() => setDock(dock === 'float' ? 'right' : 'float')}
            >
              {dock === 'float' ? (
                <PanelRight className="h-3.5 w-3.5" />
              ) : (
                <PictureInPicture2 className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Réduire"
            data-copilot-id="companion-minimize"
            onClick={minimize}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Fermer"
            data-copilot-id="companion-close"
            onClick={closeWindow}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Body — the existing copilot content, unchanged */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>

      {/* SE resize handle (float only) */}
      {effectiveDock === 'float' && (
        <div
          data-copilot-id="companion-resize-handle"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
          style={{ touchAction: 'none' }}
          title="Redimensionner"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4 text-muted-foreground/50">
            <path d="M14 8L8 14M14 12l-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  )
}

export default CompanionWindow
